// Explore — the A/B calculator, Curro-redesigned.
// Single mode: one filter set, KPI count-ups, breakdown as a bar CHART with a
// chart/table toggle (exact totals preserved). Compare mode: View A (blue) vs
// View B (violet) side by side with Δ and Δ% — each set has its own campus
// chips, date presets AND (for admins) its own SNAPSHOT, so "this month's dump
// vs last month's dump" is a first-class comparison. Tenants stay force-scoped
// server-side regardless of anything chosen here. Data wiring (AnalyticsApi,
// useRun, useAuth) is unchanged from the previous version.

import { useCallback, useEffect, useState } from "react";
import { AnalyticsApi } from "../hooks/useApi.js";
import { useRun } from "../hooks/useRun.jsx";
import { CountUp, PairedBars, DivergingDelta, CompareDonuts, GroupedColumns } from "../components/Charts.jsx";
import SearchSelect from "../components/SearchSelect.jsx";
import Icon from "../components/Icon.jsx";
import { fmtR, fmtRm, fmtN, PALETTE } from "../lib/format.js";

const GROUPS = [
  { value: "campus",     label: "Campus",             icon: "building" },
  { value: "category",   label: "Expense category",   icon: "tag" },
  { value: "source",     label: "Procurement method", icon: "card" },
  { value: "vendor",     label: "Vendor",             icon: "store" },
  { value: "month",      label: "Month",              icon: "calendar" },
  { value: "businessEx", label: "Business Ex",        icon: "layers" },
];
// Secondary dimension filters (beyond campus + date), shown under "More filters".
// Each maps a filter-set key → its /api/meta option list + display noun.
// `rev` dims get a spend-ranked "Top 10 by spend" quick-pick + spend column.
const FILTER_DIMS = [
  { key: "categories", metaKey: "categories", label: "Expense category",   noun: { singular: "category",          plural: "expense categories" } },
  { key: "sources",    metaKey: "sources",    label: "Procurement method", noun: { singular: "method",            plural: "procurement methods" } },
  { key: "vendors",    metaKey: "vendors",    label: "Vendor",             noun: { singular: "vendor",            plural: "vendors" }, rev: true },
  { key: "businessEx", metaKey: "businessEx", label: "Business Ex",        noun: { singular: "Business Ex value", plural: "Business Ex values" } },
];
const ROW_CAP = 100;       // table cap (Others row keeps totals exact)
const CHART_CAP = 14;      // chart cap (+N more row keeps totals exact)

const fmtDeltaPct = (v) => (v >= 0 ? "+" : "") + (v * 100).toFixed(1) + "%";
const EMPTY_SET = { campuses: [], categories: [], sources: [], vendors: [], businessEx: [], from: "", to: "", runId: "" };

const shortMonth = (iso) => new Date(iso).toLocaleDateString("en-ZA", { month: "short" });

/** Date presets derived from the snapshot's real coverage. */
function presetsFor(meta) {
  const all = { id: "all", label: "Full range", from: "", to: "" };
  if (!meta?.dateMin || !meta?.dateMax) return [all];
  const d1 = new Date(meta.dateMin), d2 = new Date(meta.dateMax);
  if (!(d1 < d2)) return [all];
  const mid = new Date((d1.getTime() + d2.getTime()) / 2);
  const midEnd = new Date(Date.UTC(mid.getUTCFullYear(), mid.getUTCMonth() + 1, 0));
  const midNext = new Date(Date.UTC(mid.getUTCFullYear(), mid.getUTCMonth() + 1, 1));
  const iso = (d) => d.toISOString().slice(0, 10);
  return [
    all,
    { id: "h1", label: `${shortMonth(meta.dateMin)}–${shortMonth(iso(midEnd))}`, from: meta.dateMin, to: iso(midEnd) },
    { id: "h2", label: `${shortMonth(iso(midNext))}–${shortMonth(meta.dateMax)}`, from: iso(midNext), to: meta.dateMax },
  ];
}

function FilterSet({ title, icon = "filter", accent = "var(--primary)", value, onChange, meta, runs, campusRevenue = {}, vendorRevenue = {}, withSnapshot = false }) {
  const presets = presetsFor(meta);
  const matched = presets.find((p) => p.from === value.from && p.to === value.to);
  const activePresetId = matched ? matched.id : "custom";
  const [showCustom, setShowCustom] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const customOn = activePresetId === "custom" || showCustom;

  const moreActive = FILTER_DIMS.reduce((n, d) => n + (value[d.key]?.length ?? 0), 0);
  const anyActive = value.campuses.length > 0 || value.from || value.to || value.runId || moreActive > 0;

  return (
    <div className="filterbar" style={{ "--fb-accent": accent }}>
      <div className="fb-head">
        <div className="fb-title"><Icon name={icon} size={14} /> {title}</div>
        {anyActive ? (
          <button type="button" className="fb-reset"
            onClick={() => { onChange({ ...EMPTY_SET }); setShowCustom(false); }}>
            <Icon name="refresh" size={13} /> Reset
          </button>
        ) : null}
      </div>

      {/* snapshot (A/B compare only) — pin a different run per view to diff
          "this month's dump vs last month's" with the same filters. The runs
          list is role-filtered SERVER-side: clients/tenants only ever receive
          clean published runs in their scope; admins also see poisoned runs
          for diagnostics. */}
      {withSnapshot && runs.length > 0 && (
        <div className="fb-section">
          <div className="fb-label"><Icon name="pin" size={12} /> Snapshot</div>
          <div className={`run-select-wrap${value.runId ? " pinned" : ""}`}
            style={{ alignSelf: "flex-start", borderColor: value.runId ? undefined : "var(--line-strong)" }}>
            <Icon name="layers" size={13} />
            <select className="run-select" style={{ maxWidth: 240 }} value={value.runId}
              onChange={(e) => onChange({ ...value, runId: e.target.value })}>
              <option value="">Live (current clean run)</option>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  #{r.id} · {r.sourceKey} · {r.verdict}{r.periodMin ? ` · ${r.periodMin}→${r.periodMax}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="fb-section">
        <div className="fb-label">Campuses
          <span className="fb-count">{value.campuses.length ? `${value.campuses.length} of ${meta?.campuses?.length ?? 0}` : "all"}</span>
        </div>
        <SearchSelect
          value={value.campuses}
          onChange={(campuses) => onChange({ ...value, campuses })}
          options={meta?.campuses ?? []}
          revenueByKey={campusRevenue}
          noun={{ singular: "campus", plural: "campuses" }}
        />
      </div>

      <div className="fb-section">
        <div className="fb-label">Period</div>
        <div className="chipset">
          {presets.map((p) => (
            <button type="button" key={p.id} className={`tchip ${activePresetId === p.id && !showCustom ? "on" : ""}`}
              onClick={() => { onChange({ ...value, from: p.from, to: p.to }); setShowCustom(false); }}>
              {activePresetId === p.id && !showCustom && <Icon name="check" size={13} />}{p.label}
            </button>
          ))}
          <button type="button" className={`tchip ${customOn ? "on" : ""}`} onClick={() => setShowCustom(!showCustom)}>
            <Icon name="calendar" size={13} /> Custom
          </button>
        </div>
        {customOn && (
          <div className="fb-dates">
            <input type="date" value={value.from} min={meta?.dateMin} max={meta?.dateMax}
              onChange={(e) => onChange({ ...value, from: e.target.value })} />
            <span className="dash"><Icon name="arrowR" size={15} /></span>
            <input type="date" value={value.to} min={meta?.dateMin} max={meta?.dateMax}
              onChange={(e) => onChange({ ...value, to: e.target.value })} />
          </div>
        )}
      </div>

      {/* More filters — category / method / vendor / businessEx (collapsed by default
          so the A/B panels stay compact). Each applies regardless of group-by;
          filtering by the dimension you group by rebases the percentages. */}
      <div className="fb-section">
        <button type="button" className="btn ghost sm" style={{ alignSelf: "flex-start" }}
          onClick={() => setShowMore((v) => !v)}>
          <Icon name={showMore ? "up" : "down"} size={13} /> {showMore ? "Fewer filters" : "More filters"}
          {moreActive > 0 && <span className="fb-count" style={{ marginLeft: 6 }}>· {moreActive} active</span>}
        </button>
      </div>
      {showMore && FILTER_DIMS.map((dim) => {
        const selCount = value[dim.key]?.length ?? 0;
        const total = (meta?.[dim.metaKey] ?? []).length;
        return (
          <div className="fb-section" key={dim.key}>
            <div className="fb-label">{dim.label}
              <span className="fb-count">{selCount ? `${selCount} of ${total}` : "all"}</span>
            </div>
            <SearchSelect
              value={value[dim.key] ?? []}
              onChange={(arr) => onChange({ ...value, [dim.key]: arr })}
              options={meta?.[dim.metaKey] ?? []}
              revenueByKey={dim.rev ? vendorRevenue : {}}
              noun={dim.noun}
            />
          </div>
        );
      })}
    </div>
  );
}

function KpiBlock({ summary }) {
  const items = [
    { ic: "spark", v: summary.netSpend, fmt: fmtR, l: "Net spend", c: "c1" },
    { ic: "table", v: summary.lines, fmt: fmtN, l: "Transaction lines", c: "c2" },
    { ic: "card", v: summary.avgLine, fmt: fmtR, l: "Avg line value", c: "c3" },
    { ic: "store", v: summary.vendors, fmt: fmtN, l: "Vendors", c: "c4" },
  ];
  return (
    <div className="kpis stagger">
      {items.map((k) => (
        <div className={`kpi ${k.c}`} key={k.l}>
          <div className="ic"><Icon name={k.ic} size={18} /></div>
          <div className="v"><CountUp value={k.v} fmt={k.fmt} /></div>
          <div className="l">{k.l}</div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { runId: globalRunId, runs } = useRun();   // global admin pin (topbar) + role-filtered run list

  const [meta, setMeta] = useState(null);
  const [campusRevenue, setCampusRevenue] = useState({});   // campus key → net spend, for the picker's revenue column
  const [vendorRevenue, setVendorRevenue] = useState({});   // vendor key → net spend, for the Vendor picker's Top-10
  const [groupBy, setGroupBy] = useState("campus");
  const [compare, setCompare] = useState(false);
  const [viewMode, setViewMode] = useState("chart");
  const [cmpMode, setCmpMode] = useState("chart");   // chart (dimension-aware) | delta | table
  const [setA, setSetA] = useState(EMPTY_SET);
  const [setB, setSetB] = useState(EMPTY_SET);
  const [result, setResult] = useState(null);      // {a:{summary,rows}, b?:{summary,rows}}
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // bumped on every successful fetch → re-keys the chart so a filter/group-by
  // change remounts it and the data re-animates (bars grow, donut draws, KPIs count).
  const [animKey, setAnimKey] = useState(0);

  const runFor = (s) => (s.runId ? Number(s.runId) : globalRunId);

  useEffect(() => { AnalyticsApi.meta(globalRunId).then(setMeta).catch((e) => setError(e.message)); }, [globalRunId]);
  // Whole-snapshot spend per campus + vendor (unfiltered) — powers each picker's
  // "Top 10 by spend" quick-pick and the spend column beside each option.
  useEffect(() => {
    AnalyticsApi.breakdown("campus", {}, globalRunId)
      .then((rows) => setCampusRevenue(Object.fromEntries(rows.map((r) => [r.key, r.spend]))))
      .catch(() => setCampusRevenue({}));
    AnalyticsApi.breakdown("vendor", {}, globalRunId)
      .then((rows) => setVendorRevenue(Object.fromEntries(rows.map((r) => [r.key, r.spend]))))
      .catch(() => setVendorRevenue({}));
  }, [globalRunId]);

  const run = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const fetchSet = async (s) => {
        const filter = {
          campuses: s.campuses, categories: s.categories, sources: s.sources,
          vendors: s.vendors, businessEx: s.businessEx, from: s.from, to: s.to,
        };
        const [summary, rows] = await Promise.all([
          AnalyticsApi.summary(filter, runFor(s)),
          AnalyticsApi.breakdown(groupBy, filter, runFor(s)),
        ]);
        return { summary, rows };
      };
      const a = await fetchSet(setA);
      const b = compare ? await fetchSet(setB) : null;
      setResult({ a, b, groupBy });   // stamp the dimension this data is for
      setAnimKey((k) => k + 1);   // new data → replay the chart entrance animation
    } catch (e) {
      setError(e.body?.error ?? e.message);
    } finally {
      setBusy(false);
    }
  }, [groupBy, compare, setA, setB, globalRunId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { run(); }, [run]);

  // Render the chart for the dimension the DATA is for (result.groupBy), not the
  // live selection — otherwise switching group-by swaps the chart type with stale
  // data and it animates once mid-query, then again when the new data lands.
  // (The Group-by selector still highlights the live `groupBy` for instant feedback.)
  const shownGroupBy = result?.groupBy ?? groupBy;
  const groupLabel = GROUPS.find((g) => g.value === shownGroupBy)?.label ?? shownGroupBy;

  const groupBySection = (
    <div className="fb-section">
      <div className="fb-label">Group by</div>
      <div className="segctl">
        {GROUPS.map((g) => (
          <button type="button" key={g.value} className={groupBy === g.value ? "on" : ""}
            onClick={() => setGroupBy(g.value)}>
            <Icon name={g.icon} size={15} /> {g.label}
          </button>
        ))}
      </div>
    </div>
  );

  // ---- single-mode rendering ----
  const renderSingle = ({ summary, rows }) => {
    const total = rows.reduce((s, r) => s + r.spend, 0);
    const totalLines = rows.reduce((s, r) => s + r.lines, 0);
    const pct = (sp) => (total ? ((sp / total) * 100).toFixed(1) : "0.0");

    const head = (
      <div className="bd-head">
        <h2 className="section-title" style={{ margin: 0 }}>
          {busy ? "Querying…" : <>Spend by {groupLabel.toLowerCase()} <span className="count">{rows.length}</span></>}
        </h2>
        <div className="seg sm">
          <button type="button" className={viewMode === "chart" ? "on" : ""} onClick={() => setViewMode("chart")}><Icon name="report" size={14} /> Chart</button>
          <button type="button" className={viewMode === "table" ? "on" : ""} onClick={() => setViewMode("table")}><Icon name="table" size={14} /> Table</button>
        </div>
      </div>
    );

    if (viewMode === "chart") {
      const top = rows.slice(0, CHART_CAP);
      const rest = rows.slice(CHART_CAP);
      const restSpend = rest.reduce((s, r) => s + r.spend, 0);
      const max = Math.max(1, ...top.map((r) => Math.abs(r.spend)));
      const w = (s) => `${Math.min(100, (Math.abs(s) / max) * 100).toFixed(1)}%`;
      return (
        <>
          <KpiBlock summary={summary} />
          {head}
          <div className="bd-chart" key={animKey}>
            {top.map((r, i) => (
              <div className="bd-row" key={r.key}>
                <div className="bd-label" title={r.key}>{r.key}</div>
                <div className="bd-bar"><i style={{ width: w(r.spend), background: r.spend < 0 ? "var(--neg)" : PALETTE[i % PALETTE.length] }} /></div>
                <div className="bd-fig"><b>{fmtR(r.spend)}</b><span>{pct(r.spend)}%</span></div>
              </div>
            ))}
            {rest.length > 0 && (
              <div className="bd-row muted">
                <div className="bd-label">+ {rest.length} more</div>
                <div className="bd-bar"><i style={{ width: w(restSpend), background: "var(--muted-2)" }} /></div>
                <div className="bd-fig"><b>{fmtR(restSpend)}</b><span>{pct(restSpend)}%</span></div>
              </div>
            )}
            <div className="bd-row tot">
              <div className="bd-label">Total</div>
              <div className="bd-bar" />
              <div className="bd-fig"><b>{fmtR(total)}</b><span>100%</span></div>
            </div>
          </div>
        </>
      );
    }

    const display = rows.slice(0, ROW_CAP);
    const rest = rows.slice(ROW_CAP);
    const max = Math.max(1, ...display.map((r) => Math.abs(r.spend)));
    return (
      <>
        <KpiBlock summary={summary} />
        {head}
        <div className="tbl-wrap m-cards-wrap">
          <table className="m-cards">
            <thead><tr><th>{groupLabel}</th><th className="num">Net spend</th><th className="num">Lines</th><th className="num">% of spend</th></tr></thead>
            <tbody>
              {display.map((r) => (
                <tr key={r.key}>
                  <td>
                    <div className="cell-name"><b>{r.key}</b></div>
                    <div className="rowbar"><i style={{ width: `${((Math.abs(r.spend) / max) * 100).toFixed(1)}%` }} /></div>
                  </td>
                  <td className="num" data-label="Net spend">{fmtR(r.spend)}</td>
                  <td className="num" data-label="Lines">{fmtN(r.lines)}</td>
                  <td className="num" data-label="% of spend">{pct(r.spend)}%</td>
                </tr>
              ))}
              {rest.length > 0 && (
                <tr>
                  <td><em>Others ({rest.length} more)</em></td>
                  <td className="num" data-label="Net spend">{fmtR(rest.reduce((s, r) => s + r.spend, 0))}</td>
                  <td className="num" data-label="Lines">{fmtN(rest.reduce((s, r) => s + r.lines, 0))}</td>
                  <td className="num" data-label="% of spend">{pct(rest.reduce((s, r) => s + r.spend, 0))}%</td>
                </tr>
              )}
              <tr className="total">
                <td>Total ({rows.length})</td>
                <td className="num" data-label="Net spend">{fmtR(total)}</td>
                <td className="num" data-label="Lines">{fmtN(totalLines)}</td>
                <td className="num" data-label="% of spend">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    );
  };

  // ---- compare-mode rendering (Δ = B − A, Δ% vs A) ----
  const renderCompare = ({ a, b }) => {
    const map = new Map();
    for (const r of a.rows) map.set(r.key, { a: r.spend, b: 0 });
    for (const r of b.rows) { const e = map.get(r.key) ?? { a: 0, b: 0 }; e.b = r.spend; map.set(r.key, e); }
    const rows = [...map.entries()].map(([k, v]) => ({ k, a: v.a, b: v.b, d: v.b - v.a }))
      .sort((x, y) => Math.abs(y.a) + Math.abs(y.b) - (Math.abs(x.a) + Math.abs(x.b)));
    const ta = rows.reduce((s, r) => s + r.a, 0), tb = rows.reduce((s, r) => s + r.b, 0);
    const display = rows.slice(0, ROW_CAP), rest = rows.slice(ROW_CAP);
    const rowEl = (k, av, bv, d, key) => {
      const p = av ? d / Math.abs(av) : (bv ? 1 : 0);
      const cls = d >= 0 ? "pos" : "neg";
      return (
        <tr key={key}>
          <td><b style={{ color: "var(--ink)" }}>{k}</b></td>
          <td className="num" data-label="View A">{fmtR(av)}</td>
          <td className="num" data-label="View B">{fmtR(bv)}</td>
          <td className={`num ${cls}`} data-label="Δ (B−A)">{fmtR(d)}</td>
          <td className={`num ${cls}`} data-label="Δ %">{fmtDeltaPct(p)}</td>
        </tr>
      );
    };
    const td = tb - ta, tcls = td >= 0 ? "pos" : "neg";
    const diff = b.summary.netSpend - a.summary.netSpend;

    // rows for the charts: align A/B by key with signed Δ + Δ%
    const deltaRows = rows.map((r) => ({ key: r.k, a: r.a, b: r.b, delta: r.b - r.a, pct: r.a ? ((r.b - r.a) / Math.abs(r.a)) * 100 : null }));
    const dim = shownGroupBy;
    const isDonutDim = dim === "source" || dim === "businessEx";
    const isMonthDim = dim === "month";
    const primaryLabel = isDonutDim ? "Share" : isMonthDim ? "Timeline" : "Paired";
    const primaryIcon = isDonutDim ? "compass" : isMonthDim ? "calendar" : "report";
    // donut sides share one key order so palette index lines up (share-shift legible)
    const sharedOrder = [...deltaRows].sort((x, y) => (y.a + y.b) - (x.a + x.b)).map((r) => r.key);
    const donutA = sharedOrder.map((k) => ({ key: k, spend: (deltaRows.find((z) => z.key === k) || {}).a || 0 }));
    const donutB = sharedOrder.map((k) => ({ key: k, spend: (deltaRows.find((z) => z.key === k) || {}).b || 0 }));
    // months arrive from the API already chronological ("Jan 2025", …) — preserve
    // that order (never alpha-sort the labels); union A+B keeping first-seen order.
    const monthOrder = [...a.rows.map((r) => r.key), ...b.rows.map((r) => r.key)]
      .filter((k, i, arr) => k !== "(none)" && arr.indexOf(k) === i);

    return (
      <>
        <div className="kpis" style={{ marginTop: 18 }}>
          <div className="kpi c1"><div className="ic"><Icon name="spark" size={18} /></div>
            <div className="v"><CountUp value={a.summary.netSpend} fmt={fmtRm} /></div><div className="l">View A net spend · {fmtN(a.summary.lines)} lines</div></div>
          <div className="kpi c3"><div className="ic"><Icon name="spark" size={18} /></div>
            <div className="v"><CountUp value={b.summary.netSpend} fmt={fmtRm} /></div><div className="l">View B net spend · {fmtN(b.summary.lines)} lines</div></div>
          <div className="kpi"><div className="ic" style={{ background: "var(--surface-2)" }}><Icon name={diff >= 0 ? "up" : "down"} size={18} /></div>
            <div className="v" style={{ color: diff >= 0 ? "var(--pos)" : "var(--neg)" }}>{diff >= 0 ? "+" : "−"}{fmtRm(Math.abs(diff))}</div>
            <div className="l">B − A difference</div></div>
          <div className="kpi"><div className="ic" style={{ background: "var(--surface-2)" }}><Icon name="spark" size={18} /></div>
            <div className="v">{a.summary.netSpend ? ((diff / Math.abs(a.summary.netSpend)) * 100).toFixed(1) : "0.0"}%</div>
            <div className="l">Relative change</div></div>
        </div>

        <div className="bd-head">
          <h2 className="section-title" style={{ margin: 0 }}>
            {busy ? "Querying…" : <>A vs B by {groupLabel.toLowerCase()} <span className="count">{rows.length}</span></>}
          </h2>
          <div className="seg sm">
            <button type="button" className={cmpMode === "chart" ? "on" : ""} onClick={() => setCmpMode("chart")}><Icon name={primaryIcon} size={14} /> {primaryLabel}</button>
            <button type="button" className={cmpMode === "delta" ? "on" : ""} onClick={() => setCmpMode("delta")}><Icon name="bolt" size={14} /> Δ change</button>
            <button type="button" className={cmpMode === "table" ? "on" : ""} onClick={() => setCmpMode("table")}><Icon name="table" size={14} /> Table</button>
          </div>
        </div>

        {cmpMode === "table" ? (
          <div className="tbl-wrap m-cards-wrap">
            <table className="m-cards">
              <thead><tr><th>{groupLabel}</th><th className="num">View A</th><th className="num">View B</th><th className="num">Δ (B−A)</th><th className="num">Δ %</th></tr></thead>
              <tbody>
                {display.map((r) => rowEl(r.k, r.a, r.b, r.d, r.k))}
                {rest.length > 0 && rowEl(
                  <em>Others ({rest.length} more)</em>,
                  rest.reduce((s, r) => s + r.a, 0), rest.reduce((s, r) => s + r.b, 0),
                  rest.reduce((s, r) => s + r.b - r.a, 0), "__others")}
                <tr className="total">
                  <td>Total</td>
                  <td className="num" data-label="View A">{fmtR(ta)}</td>
                  <td className="num" data-label="View B">{fmtR(tb)}</td>
                  <td className={`num ${tcls}`} data-label="Δ (B−A)">{fmtR(td)}</td>
                  <td className={`num ${tcls}`} data-label="Δ %">{fmtDeltaPct(ta ? td / Math.abs(ta) : 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : cmpMode === "delta" ? (
          <DivergingDelta key={animKey} rows={deltaRows} />
        ) : isDonutDim ? (
          <CompareDonuts key={animKey} rowsA={donutA} rowsB={donutB} />
        ) : isMonthDim ? (
          <GroupedColumns key={animKey} rows={deltaRows} order={monthOrder} />
        ) : (
          <div key={animKey}>
            <div className="ab-legend">
              <span className="it"><span className="sw a" /> View A</span>
              <span className="it"><span className="sw b" /> View B</span>
            </div>
            <PairedBars rows={deltaRows} topN={dim === "campus" ? 12 : 10} />
          </div>
        )}

        <p className="hint" style={{ marginTop: 12 }}>
          {cmpMode === "delta"
            ? <>Bars show <b>Δ (B − A)</b> per {groupLabel.toLowerCase()}, sorted by size of change — green grew, red shrank.</>
            : <>Tip: change only one thing between the views (a campus, a period{runs.length > 0 ? " or a snapshot" : ""}) to isolate its effect.</>}
        </p>
      </>
    );
  };

  return (
    <div className="rise">
      <div className="page-head">
        <div>
          <div className="eyebrow">Explore</div>
          <h1>Build a view</h1>
          <p className="lead">Slice the published snapshot by any dimension, filter by campus and date, and compare two
            slices side by side. Every figure is net — credits subtracted.</p>
        </div>
        <button type="button" className={`btn ${compare ? "primary" : "ghost"}`}
          onClick={() => {
            // leaving compare: drop per-view snapshot pins (their picker is compare-only)
            if (compare) { setSetA((s) => ({ ...s, runId: "" })); setSetB((s) => ({ ...s, runId: "" })); }
            setCompare(!compare);
          }}>
          <Icon name="layers" size={16} /> {compare ? "Comparing A / B" : "Compare A / B"}
        </button>
      </div>

      {!compare ? (
        <div className="panel">
          <div className="filterbar">
            {groupBySection}
          </div>
          <div style={{ height: 18 }} />
          <FilterSet title="Filters" value={setA} onChange={setSetA} meta={meta} runs={runs} campusRevenue={campusRevenue} vendorRevenue={vendorRevenue} />
        </div>
      ) : (
        <>
          <div className="panel">
            <div className="filterbar">{groupBySection}</div>
          </div>
          <div className="cmp-cols">
            <div className="panel" style={{ borderTop: "3px solid var(--curro-blue)", margin: 0 }}>
              <FilterSet title="View A" icon="layers" value={setA} onChange={setSetA} meta={meta} runs={runs} campusRevenue={campusRevenue} vendorRevenue={vendorRevenue} withSnapshot />
            </div>
            <div className="panel" style={{ borderTop: "3px solid var(--curro-violet)", margin: 0 }}>
              <FilterSet title="View B" icon="layers" accent="var(--curro-violet)" value={setB} onChange={setSetB} meta={meta} runs={runs} campusRevenue={campusRevenue} vendorRevenue={vendorRevenue} withSnapshot />
            </div>
          </div>
        </>
      )}

      {error && <div className="error"><Icon name="alert" size={16} /> {error}</div>}
      {result && (compare && result.b ? renderCompare(result) : renderSingle(result.a))}
    </div>
  );
}
